import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import UploadZone from './components/UploadZone';
import FileQueue from './components/FileQueue';

import ValidationStatus from './components/ValidationStatus';
import UploadProgress from '../../components/ui/UploadProgress';
import NotificationToast from '../../components/ui/NotificationToast';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import contractService from '../../utils/contractService';
import StorageService from '../../utils/StorageService';

const ContractUpload = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [showValidation, setShowValidation] = useState(false);
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [processedContracts, setProcessedContracts] = useState([]);
  const [uploadOptions, setUploadOptions] = useState({
    state: 'auto',
    contractType: 'auto',
    clientName: '',
    propertyAddress: '',
    expectedClosingDate: '',
    extractDates: true,
    identifyNonStandard: true,
    generateCalendar: true,
    prepareEmails: true
  });

  // Accepted file formats and size limits - Updated to 5MB
  const acceptedFormats = ['.pdf', '.doc', '.docx'];
  const maxFileSize = 5 * 1024 * 1024; // 5MB as requested

  // Mock validation function
  const validateFile = (file) => {
    const results = [];
    
    // File size validation - Updated to 5MB with warnings
    if (file?.size > maxFileSize) {
      results?.push({
        type: 'error',
        fileName: file?.name,
        message: 'File size exceeds 5MB limit',
        details: `Current size: ${(file?.size / 1024 / 1024)?.toFixed(2)}MB. Please compress your file or use a smaller version.`
      });
    } else if (file?.size > 3 * 1024 * 1024) { // Warning for files over 3MB
      results?.push({
        type: 'warning',
        fileName: file?.name,
        message: 'Large file detected',
        details: `File size: ${(file?.size / 1024 / 1024)?.toFixed(2)}MB. Consider compressing for faster processing.`
      });
    }

    // File format validation
    const extension = file?.name?.split('.')?.pop()?.toLowerCase();
    if (!['pdf', 'doc', 'docx']?.includes(extension)) {
      results?.push({
        type: 'error',
        fileName: file?.name,
        message: 'Unsupported file format',
        details: 'Only PDF, DOC, and DOCX files are supported'
      });
    }

    // Mock content validation
    if (file?.size < 1024) {
      results?.push({
        type: 'warning',
        fileName: file?.name,
        message: 'File appears to be very small',
        details: 'This may not contain sufficient contract data'
      });
    }

    // Success validation
    if (results?.length === 0) {
      results?.push({
        type: 'success',
        fileName: file?.name,
        message: 'File format and size validated successfully',
        details: 'Ready for contract analysis'
      });
    }

    return results;
  };

  // Handle file selection with auto-analyze
  const handleFilesSelected = async (selectedFiles) => {
    // 🔥 DEBUG: Log file selection
    console.log('🔥 FILE SELECTION - selectedFiles:', selectedFiles);
    console.log('🔥 FILE SELECTION - selectedFiles.length:', selectedFiles?.length);
    
    const newFiles = selectedFiles?.map((file, index) => ({
      id: Date.now() + index,
      name: file?.name,
      size: file?.size,
      file: file,
      status: 'pending',
      progress: 0
    }));

    console.log('🔥 FILE SELECTION - newFiles created:', newFiles);
    
    setFiles(prevFiles => {
      const updatedFiles = [...prevFiles, ...newFiles];
      console.log('🔥 FILE SELECTION - Setting files to:', updatedFiles);
      return updatedFiles;
    });

    // Validate files
    const allValidationResults = [];
    selectedFiles?.forEach(file => {
      const results = validateFile(file);
      allValidationResults?.push(...results);
    });

    setValidationResults(prevResults => [...prevResults, ...allValidationResults]);
    setShowValidation(true);

    // Show notification
    //     addNotification({
    //       type: 'info',
    //       title: 'Files Added',
    //       message: `${selectedFiles?.length} file(s) added to upload queue`,
    //       duration: 3000
    //     });

    // Auto-analyze valid files after a short delay
    const validFiles = selectedFiles?.filter(file => {
      const fileResults = validateFile(file);
      return !fileResults?.some(result => result?.type === 'error');
    });

    if (validFiles?.length > 0) {
      console.log('🔥 AUTO-ANALYZE - Starting auto-processing in 2 seconds for:', validFiles?.length, 'files');
      setTimeout(() => {
        console.log('🔥 AUTO-ANALYZE - About to call handleStartProcessing() with files:', newFiles);
        handleStartProcessingWithFiles(newFiles);
      }, 2000);
    } else {
      console.log('🔥 AUTO-ANALYZE - No valid files to auto-process');
    }
  };

  // Remove file from queue
  const handleRemoveFile = (index) => {
    const removedFile = files?.[index];
    setFiles(prevFiles => prevFiles?.filter((_, i) => i !== index));
    
    // Remove validation results for this file
    setValidationResults(prevResults => 
      prevResults?.filter(result => result?.fileName !== removedFile?.name)
    );

    //     addNotification({
    //       type: 'info',
    //       message: `${removedFile?.name} removed from queue`,
    //       duration: 2000
    //     });
  };

  // Clear all files
  const handleClearAll = () => {
    setFiles([]);
    setValidationResults([]);
    setShowValidation(false);
    
    //     addNotification({
    //       type: 'info',
    //       message: 'All files cleared from queue',
    //       duration: 2000
    //     });
  };

  // Process files directly (avoiding state race condition)
  const handleStartProcessingWithFiles = async (filesToProcess) => {
    console.log('🔥 DIRECT PROCESSING - filesToProcess:', filesToProcess);
    
    if (!filesToProcess || filesToProcess.length === 0) {
    //       addNotification({
    //         type: 'error',
    //         title: 'No Files',
    //         message: 'No files provided for processing',
    //         duration: 5000
    //       });
      return;
    }

    setIsProcessing(true);
    setAnalysisComplete(false);
    const processedResults = [];
    
    // Process each file with real AI analysis
    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      
      try {
        // Update file status to processing
        setFiles(prevFiles => 
          prevFiles?.map(f => 
            f?.id === file?.id ? { ...f, status: 'processing' } : f
          )
        );

        // Show upload progress
        setUploadProgress({
          progress: 0,
          status: 'uploading',
          filename: file?.name
        });

        // Simulate upload progress
        for (let progress = 0; progress <= 50; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          setUploadProgress(prev => ({ ...prev, progress }));
          
          setFiles(prevFiles => 
            prevFiles?.map(f => 
              f?.id === file?.id ? { ...f, progress } : f
            )
          );
        }

        // Switch to AI processing status
        setUploadProgress(prev => ({ ...prev, status: 'processing' }));
        
        // Store file data for download capability using storage manager
        const storageResult = await StorageService.uploadContract(file?.file, 'local', null);
        if (storageResult.success) {
          console.log('✅ File stored for download:', file?.name);
        } else {
          console.warn('⚠️ Could not store file for download:', storageResult.message);
        }

        // Process contract with real AI
        const processingOptions = {
          clientName: uploadOptions?.clientName,
          clientType: uploadOptions?.contractType === 'auto' ? 'buyer' : uploadOptions?.contractType,
          extractDates: uploadOptions?.extractDates,
          identifyNonStandard: uploadOptions?.identifyNonStandard
        };

        // 🔥 DEBUG: Log processing attempt
        console.log('🔥 PROCESSING - Starting contract processing for:', file?.name);
        
        const result = await contractService?.processContract(file?.file, processingOptions);
        
        // 🔥 DEBUG: Log processing result
        console.log('🔥 PROCESSING - Result received:', result);
        console.log('🔥 PROCESSING - Result success:', result?.success);
        console.log('🔥 PROCESSING - Result data:', result?.data);
        console.log('🔥 PROCESSING - Result data extractedData:', result?.data?.extractedData);
        console.log('🔥 PROCESSING - Property address:', result?.data?.extractedData?.property?.address);
        
        // Continue progress simulation during AI processing
        for (let progress = 50; progress <= 100; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 300));
          setUploadProgress(prev => ({ ...prev, progress }));
          
          setFiles(prevFiles => 
            prevFiles?.map(f => 
              f?.id === file?.id ? { ...f, progress } : f
            )
          );
        }

        if (result?.success) {
          console.log('🔥 PROCESSING SUCCESS - Adding to processedResults:', result?.data);
          console.log('🔥 PROCESSING SUCCESS - result.data.extractedData:', result?.data?.extractedData);
          console.log('🔥 PROCESSING SUCCESS - Property address from result:', result?.data?.extractedData?.property?.address);
          
          console.log('🔥 PROCESSING SUCCESS - Data processed successfully');
          
          // Complete processing successfully
          setFiles(prevFiles => 
            prevFiles?.map(f => 
              f?.id === file?.id ? { ...f, status: 'complete', progress: 100 } : f
            )
          );
          
          processedResults?.push(result?.data);
          
    //           addNotification({
    //             type: 'success',
    //             title: 'Analysis Complete',
    //             message: `${file?.name} processed successfully`,
    //             duration: 3000
    //           });
        } else {
          // Handle processing error
          setFiles(prevFiles => 
            prevFiles?.map(f => 
              f?.id === file?.id ? { ...f, status: 'error', error: result?.error, progress: 0 } : f
            )
          );
          
    //           addNotification({
    //             type: 'error',
    //             title: 'Processing Failed',
    //             message: `${file?.name}: ${result?.error}`,
    //             duration: 5000
    //           });
        }

      } catch (error) {
        console.error('Processing error:', error);
        
        setFiles(prevFiles => 
          prevFiles?.map(f => 
            f?.id === file?.id ? { ...f, status: 'error', error: error?.message, progress: 0 } : f
          )
        );
        
    //         addNotification({
    //           type: 'error',
    //           title: 'Processing Error',
    //           message: `Failed to process ${file?.name}`,
    //           duration: 5000
    //         });
      }

      setUploadProgress(prev => ({ ...prev, status: 'complete' }));
      
      // Hide progress after completion
      setTimeout(() => {
        setUploadProgress(null);
      }, 1000);
    }

    // 🔥 DEBUG: Log completion status
    console.log('🔥 PROCESSING COMPLETE - processedResults:', processedResults);
    console.log('🔥 PROCESSING COMPLETE - processedResults.length:', processedResults?.length);
    
    setIsProcessing(false);
    setProcessedContracts(processedResults);
    
    if (processedResults?.length > 0) {
      console.log('🔥 ANALYSIS COMPLETE - Setting analysisComplete to true');
      setAnalysisComplete(true);
      
    //       addNotification({
    //         type: 'success',
    //         title: 'Analysis Complete',
    //         message: `${processedResults?.length} contract(s) analyzed successfully`,
    //         duration: 5000
    //       });
    } else {
      console.log('🔥 ANALYSIS INCOMPLETE - No processed results');
    }
  };

  // Start processing files - Updated to work with FileQueue buttons
  const handleStartProcessing = async () => {
    // 🔥 DEBUG: Log initial state
    console.log('🔥 START PROCESSING - files:', files);
    console.log('🔥 START PROCESSING - validationResults:', validationResults);
    
    const validFiles = files?.filter(file => 
      !validationResults?.some(result => 
        result?.fileName === file?.name && result?.type === 'error'
      )
    );

    console.log('🔥 START PROCESSING - validFiles:', validFiles);
    console.log('🔥 START PROCESSING - validFiles.length:', validFiles?.length);

    if (!validFiles || validFiles.length === 0) {
    //       addNotification({
    //         type: 'error',
    //         title: 'No Valid Files',
    //         message: 'Please fix validation errors before processing',
    //         duration: 5000
    //       });
      return;
    }

    setIsProcessing(true);
    setAnalysisComplete(false);
    const processedResults = [];
    
    // Process each file with real AI analysis
    for (let i = 0; i < validFiles?.length; i++) {
      const file = validFiles?.[i];
      
      try {
        // Update file status to processing
        setFiles(prevFiles => 
          prevFiles?.map(f => 
            f?.id === file?.id ? { ...f, status: 'processing' } : f
          )
        );

        // Show upload progress
        setUploadProgress({
          progress: 0,
          status: 'uploading',
          filename: file?.name
        });

        // Simulate upload progress
        for (let progress = 0; progress <= 50; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          setUploadProgress(prev => ({ ...prev, progress }));
          
          setFiles(prevFiles => 
            prevFiles?.map(f => 
              f?.id === file?.id ? { ...f, progress } : f
            )
          );
        }

        // Switch to AI processing status
        setUploadProgress(prev => ({ ...prev, status: 'processing' }));
        
        // Store file data for download capability using storage manager
        const storageResult = await StorageService.uploadContract(file?.file, 'local', null);
        if (storageResult.success) {
          console.log('✅ File stored for download:', file?.name);
        } else {
          console.warn('⚠️ Could not store file for download:', storageResult.message);
        }

        // Process contract with real AI
        const processingOptions = {
          clientName: uploadOptions?.clientName,
          clientType: uploadOptions?.contractType === 'auto' ? 'buyer' : uploadOptions?.contractType,
          extractDates: uploadOptions?.extractDates,
          identifyNonStandard: uploadOptions?.identifyNonStandard
        };

        // 🔥 DEBUG: Log processing attempt
        console.log('🔥 PROCESSING - Starting contract processing for:', file?.name);
        
        const result = await contractService?.processContract(file?.file, processingOptions);
        
        // 🔥 DEBUG: Log processing result
        console.log('🔥 PROCESSING - Result received:', result);
        console.log('🔥 PROCESSING - Result success:', result?.success);
        console.log('🔥 PROCESSING - Result data:', result?.data);
        console.log('🔥 PROCESSING - Result data extractedData:', result?.data?.extractedData);
        console.log('🔥 PROCESSING - Property address:', result?.data?.extractedData?.property?.address);
        
        // Continue progress simulation during AI processing
        for (let progress = 50; progress <= 100; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 300));
          setUploadProgress(prev => ({ ...prev, progress }));
          
          setFiles(prevFiles => 
            prevFiles?.map(f => 
              f?.id === file?.id ? { ...f, progress } : f
            )
          );
        }

        if (result?.success) {
          console.log('🔥 PROCESSING SUCCESS - Adding to processedResults:', result?.data);
          console.log('🔥 PROCESSING SUCCESS - result.data.extractedData:', result?.data?.extractedData);
          console.log('🔥 PROCESSING SUCCESS - Property address from result:', result?.data?.extractedData?.property?.address);
          
          console.log('🔥 PROCESSING SUCCESS - Data processed successfully');
          
          // Complete processing successfully
          setFiles(prevFiles => 
            prevFiles?.map(f => 
              f?.id === file?.id ? { ...f, status: 'complete', progress: 100 } : f
            )
          );
          
          processedResults?.push(result?.data);
          
    //           addNotification({
    //             type: 'success',
    //             title: 'Analysis Complete',
    //             message: `${file?.name} processed successfully`,
    //             duration: 3000
    //           });
        } else {
          // Handle processing error
          setFiles(prevFiles => 
            prevFiles?.map(f => 
              f?.id === file?.id ? { ...f, status: 'error', error: result?.error, progress: 0 } : f
            )
          );
          
    //           addNotification({
    //             type: 'error',
    //             title: 'Processing Failed',
    //             message: `${file?.name}: ${result?.error}`,
    //             duration: 5000
    //           });
        }

      } catch (error) {
        console.error('Processing error:', error);
        
        setFiles(prevFiles => 
          prevFiles?.map(f => 
            f?.id === file?.id ? { ...f, status: 'error', error: error?.message, progress: 0 } : f
          )
        );
        
    //         addNotification({
    //           type: 'error',
    //           title: 'Processing Error',
    //           message: `Failed to process ${file?.name}`,
    //           duration: 5000
    //         });
      }

      setUploadProgress(prev => ({ ...prev, status: 'complete' }));
      
      // Hide progress after completion
      setTimeout(() => {
        setUploadProgress(null);
      }, 1000);
    }

    // 🔥 DEBUG: Log completion status
    console.log('🔥 PROCESSING COMPLETE - processedResults:', processedResults);
    console.log('🔥 PROCESSING COMPLETE - processedResults.length:', processedResults?.length);
    
    setIsProcessing(false);
    setProcessedContracts(processedResults);
    
    if (processedResults?.length > 0) {
      console.log('🔥 ANALYSIS COMPLETE - Setting analysisComplete to true');
      setAnalysisComplete(true);
      
    //       addNotification({
    //         type: 'success',
    //         title: 'Analysis Complete',
    //         message: `${processedResults?.length} contract(s) analyzed successfully`,
    //         duration: 5000
    //       });
    } else {
      console.log('🔥 ANALYSIS INCOMPLETE - No processed results');
    }
  };

  // Handle view results navigation
  const handleViewResults = () => {
    if (processedContracts?.length > 0) {
      const latestContract = processedContracts?.[processedContracts?.length - 1];
      console.log('🔥 VIEWING RESULTS - latestContract:', JSON.stringify(latestContract, null, 2));
      
      // Navigate to new Summary Page if dealId is available
      if (latestContract?.dealId && latestContract?.redirectTo) {
        navigate(latestContract.redirectTo);
      } else {
        // Fallback to old results page for offline contracts
        localStorage.setItem('latestContractData', JSON.stringify(latestContract));
        navigate('/contract-analysis-results');
      }
    } else {
      navigate('/contract-analysis-results');
    }
  };

  // Add notification
  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      ...notification
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications(prev => prev?.filter(n => n?.id !== id));
  };

  // Auto-remove notifications after duration
  useEffect(() => {
    notifications?.forEach(notification => {
      if (notification?.duration && notification?.duration > 0) {
        const timer = setTimeout(() => {
          removeNotification(notification?.id);
        }, notification?.duration);
        
        return () => clearTimeout(timer);
      }
    });
  }, [notifications]);

  // Handle analysis from FileQueue component
  useEffect(() => {
    const handleStartAnalysis = () => {
      handleStartProcessing();
    };

    window.addEventListener('startAnalysis', handleStartAnalysis);
    return () => window.removeEventListener('startAnalysis', handleStartAnalysis);
  }, [files, validationResults]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Upload Progress */}
      {uploadProgress && (
        <UploadProgress
          progress={uploadProgress?.progress}
          status={uploadProgress?.status}
          filename={uploadProgress?.filename}
          onCancel={() => setUploadProgress(null)}
        />
      )}
      {/* Notifications */}
      <NotificationToast
        notifications={notifications}
        onDismiss={removeNotification}
      />
      {/* Main Content */}
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Upload" size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
            </div>
            
            <h1 className="text-3xl font-heading font-bold text-foreground mb-3">
              Upload Contract Documents
            </h1>
            
            <p className="text-lg text-muted-foreground font-body max-w-xl mx-auto leading-relaxed">
              Upload your real-estate contracts for automated analysis and client communication generation
            </p>
            
            {/* API Status Indicator */}
            <div className="mt-3 flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <span className="text-sm text-primary font-medium">
                Real AI Processing Active - Auto-Analyze Enabled
              </span>
            </div>
          </div>

          {/* Upload Zone */}
          <UploadZone
            onFilesSelected={handleFilesSelected}
            acceptedFormats={acceptedFormats}
            maxFileSize={maxFileSize}
            isProcessing={isProcessing}
          />

          {/* Validation Status */}
          <ValidationStatus
            validationResults={validationResults}
            isVisible={showValidation}
          />

          {/* File Queue */}
          <FileQueue
            files={files}
            onRemoveFile={handleRemoveFile}
            onClearAll={handleClearAll}
            isProcessing={isProcessing}
            autoAnalyze={true}
          />


          {/* View Results Button */}
          {analysisComplete && (
            <div className="w-full max-w-2xl mx-auto mt-4">
              <div className="flex items-center justify-center">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleViewResults}
                  iconName="Eye"
                  iconPosition="left"
                  iconSize={20}
                >
                  View Analysis Results
                </Button>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-xl p-8">
              <div className="text-center mb-8">
                <Icon name="HelpCircle" size={32} color="var(--color-primary)" className="mx-auto mb-4" />
                <h2 className="text-2xl font-heading font-bold text-card-foreground mb-2">
                  AI-Powered Contract Analysis
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="FileText" size={24} color="var(--color-primary)" />
                  </div>
                  <h3 className="font-heading font-semibold text-card-foreground mb-2">
                    Secure Upload
                  </h3>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Cpu" size={24} color="var(--color-accent)" />
                  </div>
                  <h3 className="font-heading font-semibold text-card-foreground mb-2">
                    Auto-Analyze
                  </h3>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Shield" size={24} color="var(--color-secondary)" />
                  </div>
                  <h3 className="font-heading font-semibold text-card-foreground mb-2">
                    Multi-State Support
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContractUpload;
